// Import core lib dependencies.
import Immutable from 'seamless-immutable';
import _ from 'lodash';

// Import action types.
import ActionTypes from './actionTypes.js';

// Config.
import Config from './config/config.js';
import Enums from './config/enums.js';

// Model entities.
import DisplayItem from '../../common/model/entity/DisplayItem/DisplayItem.js';

import * as collectionUtils from './utils/collection-utils';

// Define the initial Ugc app state.
const initialState = Immutable({});

// Initial state for a component instance.
const initialInstanceState = Immutable({
  collectionId: null,
  items:        [],
  customItems:  {},
  activeItem:   { id: null, returnFocusTo: null },
  paging:       {},
  sorting:      {},
  filtering:    {},
  polling:      {
    newItems: [],
  },
  isActiveState:          false,
  useExternalLinkHandler: false,
  isFetching:             false,
  hasFetched:             false,
});

/**
 * <pre>
 * UgcWall Reducer(s).
 *
 * Summary of responsibilities:
 *
 *  - responds to actions, receives current Redux state and modifies it accordingly.
 *
 * FYI:
 *
 * All {@link https://github.com/reactjs/redux|Redux} state modifications are handled using {@link https://github.com/rtfeldman/seamless-immutable|Seamless-immutable} objects and methods.
 * DO NOT DIRECTLY MODIFY THE REDUX STATE (Immutable should stop you from doing this, but just in case)!
 * </pre>
 *
 * @see {@link https://github.com/reactjs/redux|Redux Documentation}
 * @see {@link https://github.com/rtfeldman/seamless-immutable|Immutable Documentation}
 *
 * @author Tim Loram
 *
 * @memberof module:UgcWall
 * @namespace
 *
 * @type {Object}
 */
const Reducer = {
  /**
   * The root reducer for this module. Composes sub-reducers where logical, and passes them the appropriate sub part of the state tree.
   *
   * @param  {Object} state The current Redux Immutable state.
   * @param  {Object} action The dispatched (and received) action.
   * @return {Object} The modified Immutable Redux state.
   */
  rootReducer: (state = initialState, action) => {
    // Execute the appropriate logic based on the received action type.
    switch (action.type) {
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Register a component instance and set its initial state.
      case ActionTypes.REGISTER_INSTANCE:
        // Check and add the component type property to the state if required.
        if (!state[action.componentStateKey]) {
          state = state.set(action.componentStateKey, {});
        }

        // Path to the specific part of the state we are interested in.
        var componentTypePath = [action.componentStateKey];

        // Define the initial state.
        const instanceState = _.get(state, componentTypePath).set(action.id, initialInstanceState);
        state = state.setIn(componentTypePath, instanceState);

        // Set collection id.
        state = state.setIn([action.componentStateKey, action.id, 'collectionId'], action.initialConfig.collectionId);

        // Set initial paging, sorting and filtering states.
        state = state.setIn([action.componentStateKey, action.id, 'paging'], Immutable(action.initialConfig.paging));
        state = state.setIn([action.componentStateKey, action.id, 'sorting'], Immutable(action.initialConfig.sorting));
        state = state.setIn(
          [action.componentStateKey, action.id, 'filtering'],
          Immutable(action.initialConfig.filtering)
        );

        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Receive a set of display items.
      case ActionTypes.RECEIVE_ITEMS:
        // Call items reducer, passing it the sub part of the state it needs.
        var itemsPath = [action.componentStateKey, action.id, 'items'];
        var state = state.setIn(itemsPath, Reducer.itemsReducer(_.get(state, itemsPath), action));

        // Return modified state.
        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Receive a set of custom display items.
      case ActionTypes.RECEIVE_CUSTOM_ITEMS:
        // Call items reducer, passing it the sub part of the state it needs.
        var customItemsPath = [action.componentStateKey, action.id, 'customItems'];
        var state = state.setIn(customItemsPath, Reducer.customItemsReducer(_.get(state, customItemsPath), action));

        // Return modified state.
        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // View/close an individual item.
      case ActionTypes.VIEW_ITEM:
      case ActionTypes.CLOSE_ITEM:
      case ActionTypes.CLEAR_RETURN_FOCUS:
        // Call items reducer, passing it the sub part of the state it needs.
        var activeItemPath = [action.componentStateKey, action.id, 'activeItem'];
        var state = state.setIn(activeItemPath, Reducer.itemsReducer(_.get(state, activeItemPath), action));

        // Return modified state.
        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Update paging.
      case ActionTypes.PAGE_NEXT:
      case ActionTypes.PAGE_PREV:
      case ActionTypes.PAGE_FIRST:
      case ActionTypes.PAGE_LAST:
      case ActionTypes.UPDATE_OFFSET:
      case ActionTypes.UPDATE_PAGE_COUNT:
        // Call paging reducer, passing it the sub part of the state it needs.
        var pagingPath = [action.componentStateKey, action.id, 'paging'];
        var state = state.setIn(pagingPath, Reducer.pagingReducer(_.get(state, pagingPath), action));

        // Return modified state.
        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Update sorting.
      case ActionTypes.SORT:
        // Call sorting reducer, passing it the sub part of the state it needs.
        var sortingPath = [action.componentStateKey, action.id, 'sorting'];
        var state = state.setIn(sortingPath, Reducer.sortingReducer(_.get(state, sortingPath), action));

        // Return modified state.
        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Update filtering.
      case ActionTypes.FILTER:
        // Call filtering reducer, passing it the sub part of the state it needs.
        var filteringPath = [action.componentStateKey, action.id, 'filtering'];
        var state = state.setIn(filteringPath, Reducer.filteringReducer(_.get(state, filteringPath), action));

        // Return modified state.
        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Update active state.
      case ActionTypes.SET_ACTIVE_STATE:
        var activeStatePath = [action.componentStateKey, action.id, 'isActiveState'];
        var state = state.setIn(activeStatePath, action.isActiveState);

        // Return modified state.
        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Update collection id.
      case ActionTypes.SET_COLLECTION_ID:
        var activeStatePath = [action.componentStateKey, action.id, 'collectionId'];
        var state = state.setIn(activeStatePath, action.collectionId);

        // Return modified state.
        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Update min score.
      case ActionTypes.SET_POLLING:
        const minScorePath = [action.componentStateKey, action.id, 'polling', 'minScore'];
        const newItemsPath = [action.componentStateKey, action.id, 'polling', 'newItems'];
        state = state.setIn(minScorePath, action.minScore).setIn(newItemsPath, action.newItems);

        // Return modified state.
        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Update sorting.
      case ActionTypes.SET_SORTING:
        const sortDirPath = [action.componentStateKey, action.id, 'sorting', 'sortDir'];
        state = state.setIn(sortDirPath, action.sortDir);

        // Return modified state.
        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Update external link handler.
      case ActionTypes.SET_USE_EXTERNAL_LINK_HANDLER:
        var activeStatePath = [action.componentStateKey, action.id, 'useExternalLinkHandler'];
        var state = state.setIn(activeStatePath, action.useHandler);

        // Return modified state.
        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Update is fetching.
      case ActionTypes.IS_FETCHING:
        var isFetchingPath = [action.componentStateKey, action.id, 'isFetching'];
        var state = state.setIn(isFetchingPath, action.isFetching);

        // Return modified state.
        return state;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Update has fetched.
      case ActionTypes.HAS_FETCHED:
        var hasFetchedPath = [action.componentStateKey, action.id, 'hasFetched'];
        var state = state.setIn(hasFetchedPath, true);

        // Return modified state.
        return state;

        break;

      // Make sure to return a state even if no action was matched!
      default:
        return state;
    }
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Items reducer.
   *
   * @param state: 	{object} Sub part of the Ugc Wall state relevant to items.
   * @param action: 	{object} The current Redux action object.
   */
  itemsReducer: (state, action) => {
    // Execute the appropriate logic based on the received action type.
    switch (action.type) {
      // Receive a set of display items.
      case ActionTypes.RECEIVE_ITEMS:
        // Loop over the supplied items and create and Immutable object for each.
        const items = Immutable(_.map(action.items, item => Immutable(item, { prototype: DisplayItem.prototype })));

        // Merge new items with existing items.
        switch (action.showMoreMode) {
          case Enums.SHOW_MORE_MODE_REPLACE:
            return items;

          case Enums.SHOW_MORE_MODE_PREPEND:
            let updatedItems;
            const isCollage = action.componentStateKey === Enums.COLLAGE;

            // collage has a unique way of handling pinned content
            if (action.shouldShowStickyContent && !isCollage) {
              const [currentPinnedItems, currentNonPinnedItems] = collectionUtils.separatePinnedContent(state);
              const [newPinnedItems, newNonPinnedItems] = collectionUtils.separatePinnedContent(items);

              // make sure newest non-sticky items are first
              const pinnedItems = _.sortBy(_.concat(newPinnedItems, currentPinnedItems), item => item.meta.pinned);
              const nonPinnedItems = _.concat(newNonPinnedItems, currentNonPinnedItems);
              updatedItems = collectionUtils.resolveItems(nonPinnedItems, pinnedItems);
            } else {
              updatedItems = _.concat(items, state);
            }

            if (action.allowAutoPolling) {
              updatedItems = _.take(updatedItems, Enums.CONTENT_CAP);
            }

            return updatedItems;

          case Enums.SHOW_MORE_MODE_APPEND:
          default:
            if (action.allowAutoPolling) {
              // limit to cap when paging
              return _.take(_.concat(state, items), Enums.CONTENT_CAP);
            }
            return _.concat(state, items);
        }

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // View an individual item.
      case ActionTypes.VIEW_ITEM:
        // track view for insights dashboard
        NGX.Insights.track('view:contentitem', { i: action.itemId });

        // Update active item.
        return state.merge({ id: action.itemId, returnFocusTo: action.itemId });

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Close an individual item.
      case ActionTypes.CLOSE_ITEM:
        // Update active item.
        return state.merge({ id: null });

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // clear returnFocusTo state
      case ActionTypes.CLEAR_RETURN_FOCUS:
        return state.merge({ returnFocusTo: null });
    }

    return state;
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Custom Items reducer.
   *
   * @param state: 	{object} Sub part of the Ugc Wall state relevant to custom items.
   * @param action: 	{object} The current Redux action object.
   */
  customItemsReducer: (state, action) => {
    // Execute the appropriate logic based on the received action type.
    switch (action.type) {
      // Receive a set of custom items.
      case ActionTypes.RECEIVE_CUSTOM_ITEMS:
        const newItems = Immutable(action.items);
        return newItems;

        break;

      // Make sure to return a state even if no action was matched!
      default:
        return state;
    }
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Paging reducer.
   *
   * @param state: 	{object} Sub part of the Ugc Wall state relevant to paging.
   * @param action: 	{object} The current Redux action object.
   */
  pagingReducer: (state, action) => {
    // Execute the appropriate logic based on the received action type.
    switch (action.type) {
      // Page prev/next.
      case ActionTypes.PAGE_NEXT:
      case ActionTypes.PAGE_PREV:
      case ActionTypes.UPDATE_OFFSET:
        // Update offset.
        var newPaging = state.set('offset', action.offset);

        return newPaging;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Page first.
      case ActionTypes.PAGE_FIRST:
        // Update offset.
        var newPaging = state.set('offset', 0);

        return newPaging;

        break;

      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Update page count and current page.
      case ActionTypes.UPDATE_PAGE_COUNT:
        // Update current page and total pages.
        var newPaging = state.set('totalPages', action.totalPages);
        newPaging = newPaging.set('currentPage', action.currentPage);

        return newPaging;

        break;

      // Make sure to return a state even if no action was matched!
      default:
        return state;
    }
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Sorting reducer.
   *
   * @param state: 	{object} Sub part of the Ugc Wall state relevant to sorting.
   * @param action: 	{object} The current Redux action object.
   */
  sortingReducer: (state, action) => {
    // Execute the appropriate logic based on the received action type.
    switch (action.type) {
      // Update sorting.
      case ActionTypes.SORT:
        // Update sorting.
        const newSorting = state.merge({ sortBy: action.sortBy, sortDir: action.sortDir });

        return newSorting;

        break;

      // Make sure to return a state even if no action was matched!
      default:
        return state;
    }
  },

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  /**
   * Filtering reducer.
   *
   * @param state: 	{object} Sub part of the Ugc Wall state relevant to filtering.
   * @param action: 	{object} The current Redux action object.
   */
  filteringReducer: (state, action) => {
    // Execute the appropriate logic based on the received action type.
    switch (action.type) {
      // Update filtering.
      case ActionTypes.FILTER:
        var newFiltering = state.set('network', action.network);
        newFiltering = newFiltering.set('tag', action.tag);
        newFiltering = newFiltering.set('user', action.user);
        newFiltering = newFiltering.set('type', action.contentType);
        newFiltering = newFiltering.set('date', action.date);
        newFiltering = newFiltering.set('entry', action.entry);

        return newFiltering;

        break;

      // Make sure to return a state even if no action was matched!
      default:
        return state;
    }
  },
};

export default Reducer;
